import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { ArrowLeft, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

import overstapHeroBg from "@/assets/overstap-hero-bg.jpg";
import MigrationOrigins from "@/components/overstap/MigrationOrigins";
import MigrationTimeline from "@/components/overstap/MigrationTimeline";
import MigrationChecklist from "@/components/overstap/MigrationChecklist";
import MigrationBenefits from "@/components/overstap/MigrationBenefits";
import MigrationFAQ from "@/components/overstap/MigrationFAQ";

const WerkplekOverstap = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={overstapHeroBg}
          alt="IT-specialist begeleidt een team bij de overstap naar een nieuwe werkplek"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" />
        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/werkplek"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Digitale Werkplek
            </Link>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Soepele migratie, gegarandeerd
              </span>
            </motion.div>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <RefreshCw className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              De overstap naar je{" "}
              <span className="text-gradient">nieuwe werkplek</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Geen gedoe, geen dataverlies, geen stilstand. Wij regelen de volledige migratie — van koffiemoment tot go-live. 
              Jij werkt gewoon door, wij doen de rest.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Alles-in-één implementatie
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                € 225 per werkplek, eenmalig
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Minimale verstoring
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Waar kom je vandaan */}
      <MigrationOrigins />

      {/* 6 stappen timeline */}
      <MigrationTimeline />

      {/* Checklist + prijs */}
      <MigrationChecklist />

      {/* Voordelen */}
      <MigrationBenefits />

      {/* FAQ */}
      <MigrationFAQ />

      {/* CTA */}
      <section className="bg-muted/40 py-16 md:py-24">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-2xl"
          >
            <h2 className="text-2xl font-bold md:text-3xl">
              Klaar voor een{" "}
              <span className="text-gradient">soepele overstap?</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Neem contact op en we plannen samen de migratie. Koffie is van ons. ☕
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="werkplek-overstap-cta">
                  Neem contact op
                  <ArrowRight className="ml-2 h-4 w-4" />
                </TrackedLink>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/diensten/werkplek">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Terug naar werkplek
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WerkplekOverstap;
